import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {formatPhoneNumber} from "../../../../../../core/services/utils/utils";
import {DriversListDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import Button from "../../../../../components/base/button/button";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import {Add} from "@mui/icons-material";
import SearchInput from "../../../../../components/base/input/search_input";
import UpsertDriverDialog from "../../../../../components/dialogs/upsert-driver-dialog";
import Api from "../../../../../../core/services/api/api";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";


const exportingChunkSize = 50;

const orderByDictionary = {
    phone: "Phone",
    firstName: 'Firstname',
    lastName: 'Lastname',
    email: 'Email',
    status: 'StatusId'
}

const dataGridSaveConfig = {
    name: 'DriversViewDataGrid',
    version: 1,
}

/**
 * Drivers list action names
 */
const DriversListActionNames = {
    Edit: 'edit',
    Delete: 'delete'
}

const DriversView = () => {
    const [drivers, setDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [openUpsertDriverDialog, setOpenUpsertDriverDialog] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportWarrantyDrivers = operatorAccessLevels.packages.warrantyAndReturn.exportWarrantyDrivers;
    const {configureNotebookTags} = useConfigureNotebook('drivers-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.drivers);
        configureBehavioralTool((tags) => tags.drivers);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 100,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: true,
                pinned: true,
            },
            {
                title: "First Name",
                name: DriversListDataGridCellKeys.firstName,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: true,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "Last Name",
                name: DriversListDataGridCellKeys.lastName,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: true,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 120,
            },
            {
                title: "Phone",
                name: DriversListDataGridCellKeys.phone,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: true,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
            {
                title: "Email",
                name: DriversListDataGridCellKeys.email,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: true,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
            {
                title: "Status",
                name: DriversListDataGridCellKeys.status,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                pinned: false,
                sortable: true,
                pinnedToggleable: true,
                visibilityToggleable: true,
                width: 200,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Creates the request body for the api call
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        return {
            pagination: args.paginationInfo,
            keyword: args.filters?.keyword,
            ...(!!args.orderBy ? {
                sorts: [
                    Object.fromEntries(Object.entries(args.orderBy).map(([key, value]) =>
                        [key === "descending"
                            ? "isDesc"
                            : "Property",
                            (value === true || value === false)
                                ? value
                                : orderByDictionary[value]])
                    )
                ]
            } : {})
        }
    }, [])

    /**
     * Gets the list of drivers by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareApiRequest(args);
        const response = await Api.getListOfDrivers(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setDrivers(response.data?.items ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
                currentPage: response?.data?.pageIndex ?? 1,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, prepareApiRequest])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Opens the dialog for adding a new driver to the system
     */
    const onAddNewDriverClicked = () => {
        setSelectedDriver(null);
        setOpenUpsertDriverDialog(true);
    }

    /**
     * Searches over list of drivers in the system by setting the query params according to the received keyword
     * @param keyword
     */
    const searchOverDrivers = (keyword) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            keyword: keyword?.trim(),
        }))
    }

    /**
     * Returns the object for exporting the list of drivers by preparing the title and body of the csv file
     * @param list
     * @return {{title: string, body: (*|*[])}}
     */
    const formatExportingData = useCallback((list) => {
        return {
            title: `Drivers`,
            body: list?.map((e, index) => ({
                '': `${index + 1}`,
                'First Name': e.firstname ?? '--',
                'Last Name': e.lastname ?? '--',
                'Email': e.email ?? '--',
                'Phone': formatPhoneNumber(e.phone) ?? '--',
                "Registered": e.statusTitle ?? '--',
            })) ?? [],
        }
    }, [])

    /**
     * Exports the list of drivers in the system by calling the search function with the pagination info
     * Calls the function for formatting the data
     * @return {Promise<*>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return drivers?.map(driver => ({
            key: driver.id,
            data: driver,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [DriversListDataGridCellKeys.firstName]: driver.firstname,
                [DriversListDataGridCellKeys.lastName]: driver.lastname,
                [DriversListDataGridCellKeys.phone]: formatPhoneNumber(driver.phone),
                [DriversListDataGridCellKeys.email]: driver.email,
                [DriversListDataGridCellKeys.status]: driver.statusTitle,
            },
        }))
    }, [drivers])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: DriversListActionNames.Edit,
                title: 'Edit',
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: DriversListActionNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    /**
     * Opens dialog for editing the drivers information and sets the [selectedDriver] in the inner state
     * @param selectedDriver
     */
    const editSelectedDriver = (selectedDriver) => {
        setSelectedDriver(selectedDriver);
        setOpenUpsertDriverDialog(true);
    }

    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = (itemsToBeRemoved, allRecordsSelected) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected driver${itemsToBeRemoved?.length > 1 || allRecordsSelected ? 's' : ''}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && _removeDrivers(itemsToBeRemoved),
        );
    }

    /**
     * Calls the api regarding removing the list of drivers form the list
     * @param driverIds list of drivers to be removed
     * @private
     */
    const _removeDrivers = useCallback(async (driverIds) => {
        searchData.setLoading(true);
        let response;
        const forApi = {
            ids: driverIds
        }
        response = await Api.bulkRemoveDrivers(forApi);
        if (!isMounted())
            return;
        searchData.setLoading(false);
        if (response?.resultFlag)
            fetchEntries(searchData).then();
    }, [searchData, isMounted, fetchEntries])

    /**
     * Fetches all the drivers given the available length of the drivers.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllDrivers = async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 20,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res?.data?.items);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case DriversListActionNames.Edit: {
                if (allRowsSelected && drivers.length === 1)
                    editSelectedDriver(drivers[0]);
                else
                    editSelectedDriver(selectedRows[0].data);
                break;
            }
            case DriversListActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.id));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(drivers.map(e => e.id));
                }
                getRemoveConfirmation((await getAllDrivers(pagination.length)).map(e => e.id));
                break;
            }
            default:
                break;
        }
    }

    return (
        <>
            <div className={'drivers-list-view'}>
                <div className={'page-title-section'}>
                    <h2 className={'text-xl font-weight-semi-bold m-0'}>
                        Drivers List
                    </h2>
                    <Button
                        className={'d-flex align-items-center justify-content-center'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddNewDriverClicked}
                    >
                        <Add className='icon small mr-2'/>
                        <p className={'button-label'}>Add a new driver</p>
                    </Button>
                </div>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    initialValue={searchData.filters?.keyword}
                    search={searchOverDrivers}
                    label={'Search'}
                />
                <div className={'mb-3'}/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    footerActions={footerActions}
                    hideFooterActions={drivers?.length < 1}
                    onActionInvoked={onActionInvoked}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    getExportInfo={canExportWarrantyDrivers ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <UpsertDriverDialog
                setOpen={setOpenUpsertDriverDialog}
                open={openUpsertDriverDialog}
                selectedDriver={selectedDriver}
                searchCallback={() => search(searchData)}
            />
        </>
    );
}

export default DriversView;
